import React, { useState, useEffect } from 'react';

// Import API configuration
const getBackendUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? 'http://localhost:8000' : 'https://gradi.ai';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Save comment analysis to user history
const saveCommentAnalysisToHistory = (user, analysisResult, youtubeUrl) => {
  if (!user || !analysisResult) return;

  const userKey = user.id || user.email || user.username;
  if (!userKey) return;

  // Extract video ID from URL
  const videoIdMatch = youtubeUrl.match(/(?:v=|\/)([a-zA-Z0-9_-]{11})/);
  const videoId = videoIdMatch ? videoIdMatch[1] : Date.now().toString();
  
  const historyKey = `comment_analysis_history_${userKey}`;
  
  // Get existing history
  const savedHistory = localStorage.getItem(historyKey);
  const history = savedHistory ? JSON.parse(savedHistory) : [];

  // Check if this analysis already exists
  const existingIndex = history.findIndex(
    item => item.video_id === videoId && item.analyzedAt
  );

  const historyItem = {
    id: `comment_${videoId}_${Date.now()}`,
    video_id: videoId,
    videoTitle: analysisResult.videoTitle || 'Unknown Video',
    youtube_url: youtubeUrl,
    statistics: analysisResult.statistics || {},
    commonQuestions: analysisResult.commonQuestions || [],
    feedback: analysisResult.feedback || [],
    insights: analysisResult.insights || [],
    timestamp: new Date().toISOString(),
    analyzedAt: new Date().toISOString(),
    // Save full analysis result
    fullResult: analysisResult
  };

  if (existingIndex >= 0) {
    // Update existing entry
    history[existingIndex] = historyItem;
  } else {
    // Add new entry at the beginning
    history.unshift(historyItem);
  }

  // Keep only last 100 comment analyses
  const trimmedHistory = history.slice(0, 100);

  // Save back to localStorage
  localStorage.setItem(historyKey, JSON.stringify(trimmedHistory));
  console.log('✅ Saved comment analysis to history for user:', userKey);

  return trimmedHistory;
};

const CommentsAnalysis = ({ onPageChange, user }) => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState("");
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Load user from localStorage if not provided as prop
  useEffect(() => {
    if (user) {
      setCurrentUser(user);
    } else {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          setCurrentUser(JSON.parse(savedUser));
        } catch (e) {
          console.error('Failed to parse user from localStorage:', e);
        }
      }
    }
  }, [user]);

  // Mock data
  const mockData = {
    videoTitle: "🎯 इस Banker से समझो IBPS RRB PO & Clerk Exam फोड़ने का तरीका 😮 | Banker Success Story | Adda247",
    statistics: {
      totalComments: 25,
      averageLikes: 0.68,
      totalReplies: 3,
      sentiment: {
        positive: 80,
        neutral: 20,
        negative: 0
      }
    },
    commonQuestions: [
      "Which basic topics should one prepare in mathematics before banking exams?",
      "How to crack the Tata Crucible Challenge?",
      "Are there updates on RRB vacancies?"
    ],
    feedback: [
      "Viewers empathized with the speaker's nervousness.",
      "Strong demand for Tata Challenge content.",
      "Request for RRB vacancy update videos.",
      "Suggestion: cover foundational math topics."
    ],
    insights: [
      {
        title: "Acknowledge Speaker's Demeanor",
        detail: "Add a note if a speaker seems nervous."
      },
      {
        title: "Develop Content on Math Basics",
        detail: "Create a video/series on essential math for banking exams."
      },
      {
        title: "Explore Tata Exam Content",
        detail: "Produce content on Tata Imagination & Crucible Challenges."
      },
      {
        title: "Provide RRB Vacancy Updates",
        detail: "Timely updates via video or community post."
      },
      {
        title: "Continue Success Stories",
        detail: "High engagement suggests aspirants love success journeys."
      }
    ]
  };

  const formatCount = (value) => {
    const num = Number(value ?? 0);
    return Number.isNaN(num) ? '0' : num.toLocaleString();
  };

  // Progress animation
  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) return 95; // Cap at 95% to allow manual completion
          return prev + Math.random() * 5; // Smaller increments
        });
      }, 300);

      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [isAnalyzing]);

  const handleAnalysis = async () => {
    if (!youtubeUrl.trim()) return;

    // Validate YouTube URL
    const isValidYouTubeUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      const patterns = [
        /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)/,
        /^(https?:\/\/)?(www\.)?youtube\.com\/shorts\/[a-zA-Z0-9_-]{11}/,
        /^(https?:\/\/)?(www\.)?youtube\.com\/live\/[a-zA-Z0-9_-]{11}/,
      ];
      return patterns.some((pattern) => pattern.test(trimmed));
    };

    if (!isValidYouTubeUrl(youtubeUrl)) {
      setError("Please enter a valid YouTube URL");
      return;
    }

    setIsAnalyzing(true);
    setShowResults(false);
    setProgress(0);
    setError(null);
    setAnalysisStage("Initializing comment analysis...");

    try {
      setProgress(20);
      setAnalysisStage("Fetching video comments...");

      // Call our backend proxy for comment analysis
      const response = await fetch(`${API}/analyze-comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          youtube_url: youtubeUrl,
          analysis_type: 'comments'
        }),
        signal: AbortSignal.timeout(300000), // 5 minutes timeout
      });

      setProgress(60);
      setAnalysisStage("Processing comments data...");

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP ${response.status}: ${response.statusText}`, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      setProgress(80);
      setAnalysisStage("Analyzing sentiment and insights...");

      // Parse HTML response to extract dynamic data
      let transformedData;
      
      if (data.emailHTML) {
        // Parse HTML response from webhook
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.emailHTML, 'text/html');
        
        // Extract video title
        const videoTitleElement = doc.querySelector('h3');
        const videoTitle = videoTitleElement ? videoTitleElement.textContent.replace('Video: ', '') : data.videoTitle || youtubeUrl;
        
        // Extract statistics
        const statsList = doc.querySelectorAll('.stats ul li');
        let totalComments = 100;
        let averageLikes = 0;
        let totalReplies = 0;
        
        statsList.forEach(li => {
          const text = li.textContent;
          if (text.includes('Total Comments Analyzed:')) {
            totalComments = parseInt(text.split(':')[1].trim()) || 100;
          } else if (text.includes('Average Likes per Comment:')) {
            averageLikes = parseFloat(text.split(':')[1].trim()) || 0;
          } else if (text.includes('Total Replies:')) {
            totalReplies = parseInt(text.split(':')[1].trim()) || 0;
          }
        });
        
        // Extract common questions
        const commonQuestionsSection = Array.from(doc.querySelectorAll('.section')).find(section => 
          section.querySelector('h3')?.textContent.includes('Common Questions')
        );
        const commonQuestions = commonQuestionsSection ? 
          Array.from(commonQuestionsSection.querySelectorAll('li')).map(li => li.textContent.trim()) : [];
        
        // Extract feedback
        const feedbackSection = Array.from(doc.querySelectorAll('.section')).find(section => 
          section.querySelector('h3')?.textContent.includes('Key Feedback')
        );
        const feedback = feedbackSection ? 
          Array.from(feedbackSection.querySelectorAll('li')).map(li => li.textContent.trim()) : [];
        
        // Extract insights
        const insightsSection = Array.from(doc.querySelectorAll('.section')).find(section => 
          section.querySelector('h3')?.textContent.includes('Actionable Insights')
        );
        const insights = insightsSection ? 
          Array.from(insightsSection.querySelectorAll('li')).map(li => ({
            title: li.textContent.split(':')[0].trim(),
            detail: li.textContent.split(':').slice(1).join(':').trim()
          })) : [];
        
        // Calculate dynamic sentiment from feedback data
        const calculateSentiment = (feedbackList) => {
          let positiveCount = 0;
          let negativeCount = 0;
          let neutralCount = 0;
          
          const positiveKeywords = ['appreciation', 'praise', 'positive', 'support', 'good', 'excellent', 'great', 'love', 'amazing', 'wonderful', 'fantastic', 'brilliant', 'outstanding'];
          const negativeKeywords = ['criticism', 'negative', 'bad', 'poor', 'terrible', 'awful', 'hate', 'disappointed', 'frustrated', 'angry', 'upset', 'concerned', 'worried'];
          
          feedbackList.forEach(feedback => {
            const text = feedback.toLowerCase();
            const hasPositive = positiveKeywords.some(keyword => text.includes(keyword));
            const hasNegative = negativeKeywords.some(keyword => text.includes(keyword));
            
            if (hasPositive && !hasNegative) {
              positiveCount++;
            } else if (hasNegative && !hasPositive) {
              negativeCount++;
            } else {
              neutralCount++;
            }
          });
          
          const total = feedbackList.length || 1;
          return {
            positive: Math.round((positiveCount / total) * 100),
            neutral: Math.round((neutralCount / total) * 100),
            negative: Math.round((negativeCount / total) * 100)
          };
        };
        
        const sentiment = calculateSentiment(feedback);
        
        transformedData = {
          videoTitle: videoTitle,
          statistics: {
            totalComments: totalComments,
            averageLikes: averageLikes,
            totalReplies: totalReplies,
            sentiment: sentiment
          },
          commonQuestions: commonQuestions,
          feedback: feedback,
          insights: insights
        };
      } else {
        // Fallback to JSON format if available
        transformedData = {
          videoTitle: data.videoTitle || data.video_title || youtubeUrl,
          statistics: {
            totalComments: data.statistics?.totalComments || data.total_comments || 100,
            averageLikes: data.statistics?.averageLikes || data.average_likes || 0,
            totalReplies: data.statistics?.totalReplies || data.total_replies || 0,
            sentiment: {
              positive: data.statistics?.sentiment?.positive || data.positive_sentiment || 80,
              neutral: data.statistics?.sentiment?.neutral || data.neutral_sentiment || 15,
              negative: data.statistics?.sentiment?.negative || data.negative_sentiment || 5
            }
          },
          commonQuestions: data.commonQuestions || data.common_questions || [],
          feedback: data.feedback || data.key_feedback || [],
          insights: data.insights || data.actionable_insights || []
        };
      }

      setProgress(100);
      setAnalysisStage("Analysis completed!");
      setAnalysisResult(transformedData);
      setShowResults(true);
      
      // Save to user's comment analysis history
      if (currentUser && transformedData) {
        saveCommentAnalysisToHistory(currentUser, transformedData, youtubeUrl);
      }
      
      // Track comment analysis count for daily limits
      if (currentUser) {
        const today = new Date().toDateString();
        const userKey = currentUser.id || currentUser.email || currentUser.username;
        if (userKey) {
          const commentKey = `daily_comment_analysis_${userKey}_${today}`;
          const currentCount = parseInt(localStorage.getItem(commentKey) || '0');
          const newCount = currentCount + 1;
          localStorage.setItem(commentKey, newCount.toString());
          console.log(`📊 Comment analysis count for ${userKey}: ${newCount}/20`);
        }
      }
    } catch (error) {
      console.error('Comment analysis error:', error);
      
      // Enhanced error handling - same pattern as HTML form
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        setError("Cannot connect to analysis service. Please check your internet connection.");
      } else if (error.message.includes('HTTP 502')) {
        if (error.message.includes('webhook is not active')) {
          setError("Comment analysis webhook is not active. Using sample data for demonstration.");
        } else if (error.message.includes('workflow could not be started')) {
          setError("Comment analysis workflow could not be started. Using sample data for demonstration.");
        } else {
          setError("Comment analysis service is temporarily unavailable. Using sample data for demonstration.");
        }
      } else if (error.message.includes('HTTP 504') || error.message.includes('timeout')) {
        setError("Comment analysis is taking longer than expected. Using sample data for demonstration.");
      } else if (error.message.includes('HTTP')) {
        setError("Analysis service temporarily unavailable. Using sample data.");
      } else {
        setError(`Analysis failed: ${error.message}. Using sample data.`);
      }
      
      // Fallback to mock data if webhook fails
      setProgress(100);
      setAnalysisStage("Using sample data...");
      setAnalysisResult(mockData);
      setShowResults(true);
      
      // Note: Don't save mock data to history to avoid polluting user's history
    } finally {
      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisStage("");
      }, 500);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isAnalyzing) {
      handleAnalysis();
    }
  };

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container comments-analysis">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          <h1 className="hero-title">
            <span 
              onClick={() => onPageChange && onPageChange('home')}
              style={{
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#a855f7';
                e.target.style.textShadow = '0 0 20px rgba(168, 85, 247, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '';
                e.target.style.textShadow = '';
              }}
            >
              Gradi.AI
            </span>
          </h1>
          <p className="hero-subtitle">
            YouTube Comments Insights
          </p>
          <p className="hero-description">
            Paste Your Video Link & Get Comment Analysis
          </p>

          {/* Input Section */}
          <div className="input-section">
            <div className={`input-container ${showResults ? 'with-results' : ''}`} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Paste Your YouTube Link Here"
                value={youtubeUrl}
                onChange={(e) => {
                  setYoutubeUrl(e.target.value);
                  setError(null); // Clear error when user types
                }}
                onKeyDown={handleKeyDown}
                disabled={isAnalyzing}
                className="input-field"
                style={{ flex: 1 }}
              />
              <button
                onClick={handleAnalysis}
                disabled={isAnalyzing || !youtubeUrl.trim()}
                className="analyze-button"
                style={{ flexShrink: 0 }}
              >
                {isAnalyzing ? (
                  <>
                    <span className="spinner" />
                    Analyzing...
                  </>
                ) : (
                  'Analyze'
                )}
              </button>
            </div>
            
            {/* Error Display */}
            {error && !isAnalyzing && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: (error.includes('webhook is not active') || error.includes('workflow could not be started'))
                  ? 'rgba(251, 191, 36, 0.1)' 
                  : 'rgba(239, 68, 68, 0.1)',
                border: (error.includes('webhook is not active') || error.includes('workflow could not be started'))
                  ? '1px solid rgba(251, 191, 36, 0.3)'
                  : '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                color: (error.includes('webhook is not active') || error.includes('workflow could not be started')) ? '#f59e0b' : '#ef4444',
                textAlign: 'center',
                fontSize: '0.95rem'
              }}>
                {error}
                {(error.includes('webhook is not active') || error.includes('workflow could not be started')) && (
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}>
                    {error.includes('webhook is not active') 
                      ? "The webhook needs to be activated in the n8n workflow to enable live comment analysis."
                      : "The n8n workflow configuration needs to be checked to enable live comment analysis."
                    }
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Progress Section */}
        {isAnalyzing && analysisStage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(15px)',
            animation: 'fadeIn 0.5s ease-out'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.95), rgba(30, 30, 60, 0.95))',
              border: '2px solid rgba(139, 92, 246, 0.4)',
              borderRadius: '32px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
              animation: 'slideIn 0.5s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Animated Background Elements */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
                animation: 'rotate 20s linear infinite'
              }} />
              
              {/* Professional Icon */}
              <div style={{
                fontSize: '3rem',
                marginBottom: '2rem',
                animation: 'float 4s ease-in-out infinite',
                filter: 'drop-shadow(0 0 20px rgba(139, 92, 246, 0.5))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '3px solid #8b5cf6',
                  borderTop: '3px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 2s linear infinite'
                }} />
              </div>

              {/* Main Text */}
              <h2 style={{
                fontSize: '1.8rem',
                fontWeight: '800',
                color: '#e5e7eb',
                marginBottom: '1rem',
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'glow 3s ease-in-out infinite alternate'
              }}>
                {analysisStage}
              </h2>

              {/* Progress Bar */}
              <div style={{
                width: '100%',
                height: '12px',
                background: 'rgba(30, 30, 60, 0.8)',
                borderRadius: '10px',
                overflow: 'hidden',
                marginBottom: '1rem',
                position: 'relative'
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #8b5cf6, #ec4899, #06b6d4)',
                  borderRadius: '10px',
                  transition: 'width 0.5s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Shimmer Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    animation: 'shimmer 2s infinite'
                  }} />
                </div>
              </div>

              {/* Percentage */}
              <div style={{
                color: '#06b6d4',
                fontSize: '1.2rem',
                fontWeight: '700',
                marginBottom: '2rem'
              }}>
                {Math.round(progress)}% Complete
              </div>

              {/* Floating Particles */}
              <div style={{
                position: 'absolute',
                top: '20%',
                left: '10%',
                width: '6px',
                height: '6px',
                background: '#8b5cf6',
                borderRadius: '50%',
                animation: 'floatParticle 6s ease-in-out infinite',
                opacity: 0.4
              }} />
              <div style={{
                position: 'absolute',
                top: '60%',
                right: '15%',
                width: '4px',
                height: '4px',
                background: '#ec4899',
                borderRadius: '50%',
                animation: 'floatParticle 5s ease-in-out infinite 1s',
                opacity: 0.4
              }} />
              <div style={{
                position: 'absolute',
                bottom: '30%',
                left: '20%',
                width: '8px',
                height: '8px',
                background: '#06b6d4',
                borderRadius: '50%',
                animation: 'floatParticle 7s ease-in-out infinite 2s',
                opacity: 0.4
              }} />
            </div>
          </div>
        )}

        {/* Results Section */}
        {showResults && analysisResult && (
          <div className="results-section">
            {/* Video Title */}
            <div style={{
              textAlign: 'center',
              marginBottom: '3rem'
            }}>
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '800',
                marginBottom: '1rem',
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                📊 Comments Analysis Report
              </h2>
              <p style={{
                color: '#9ca3af',
                fontSize: '1.1rem',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}>
                {analysisResult.videoTitle}
              </p>
            </div>

            {/* Quick Statistics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '2rem',
              marginBottom: '3rem'
            }}>
              <div style={{
                background: 'rgba(15, 15, 35, 0.9)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '24px',
                padding: '2rem',
                textAlign: 'center',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
              }}>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '1rem',
                  fontWeight: '600',
                  letterSpacing: '0.02em'
                }}>
                  Comments Analyzed
                </div>
                <div style={{
                  marginTop: '1rem',
                  color: '#e5e7eb',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  Video Comments:&nbsp;
                  <span style={{ color: '#8b5cf6', fontWeight: '700' }}>
                    {formatCount(analysisResult.statistics.totalComments)}
                  </span>
                </div>
                <div style={{
                  marginTop: '0.5rem',
                  color: '#e5e7eb',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  Replies:&nbsp;
                  <span style={{ color: '#ec4899', fontWeight: '700' }}>
                    {formatCount(analysisResult.statistics.totalReplies)}
                  </span>
                </div>
              </div>

              <div style={{
                background: 'rgba(15, 15, 35, 0.9)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '24px',
                padding: '2rem',
                textAlign: 'center',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '900',
                  color: '#06b6d4',
                  marginBottom: '0.5rem'
                }}>
                  {analysisResult.statistics.averageLikes}
                </div>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}>
                  Avg Likes/Comment
                </div>
              </div>

              <div style={{
                background: 'rgba(15, 15, 35, 0.9)',
                border: '2px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '24px',
                padding: '2rem',
                textAlign: 'center',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
              }}>
                <div style={{
                  fontSize: '2.2rem',
                  fontWeight: '900',
                  color: '#ec4899',
                  marginBottom: '0.5rem'
                }}>
                  {analysisResult.statistics.totalComments > 0
                    ? (
                      analysisResult.statistics.totalReplies /
                      Math.max(analysisResult.statistics.totalComments, 1)
                    ).toFixed(2)
                    : '0.00'}
                </div>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}>
                  Replies per Comment
                </div>
              </div>

              <div style={{
                background: 'rgba(15, 15, 35, 0.9)',
                border: '2px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '24px',
                padding: '2rem',
                textAlign: 'center',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
              }}>
                  <div style={{
                    fontSize: 'clamp(1.8rem, 4vw, 2.2rem)',
                    fontWeight: '900',
                    color: '#10b981',
                    marginBottom: '0.5rem'
                  }}>
                    {analysisResult.statistics.sentiment.positive}%
                  </div>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}>
                  Positive Sentiment
                </div>
              </div>
            </div>

            {/* Sentiment Distribution */}
            <div style={{
              background: 'rgba(15, 15, 35, 0.9)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '24px',
              padding: '3rem',
              marginBottom: '3rem',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
            }}>
              <h3 style={{
                fontSize: '1.8rem',
                fontWeight: '700',
                marginBottom: '2rem',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                📈 Sentiment Distribution
              </h3>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '3rem',
                flexWrap: 'wrap'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.8rem',
                    fontWeight: '900',
                    color: 'white',
                    boxShadow: '0 15px 30px rgba(16, 185, 129, 0.4)'
                  }}>
                    {analysisResult.statistics.sentiment.positive}%
                  </div>
                  <div style={{
                    color: '#10b981',
                    fontSize: '1.2rem',
                    fontWeight: '600'
                  }}>
                    Positive
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1.4rem',
                    fontWeight: '900',
                    color: 'white',
                    boxShadow: '0 15px 30px rgba(107, 114, 128, 0.4)'
                  }}>
                    {analysisResult.statistics.sentiment.neutral}%
                  </div>
                  <div style={{
                    color: '#6b7280',
                    fontSize: '1.2rem',
                    fontWeight: '600'
                  }}>
                    Neutral
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    fontSize: '1rem',
                    fontWeight: '900',
                    color: 'white',
                    boxShadow: '0 15px 30px rgba(239, 68, 68, 0.4)'
                  }}>
                    {analysisResult.statistics.sentiment.negative}%
                  </div>
                  <div style={{
                    color: '#ef4444',
                    fontSize: '1.2rem',
                    fontWeight: '600'
                  }}>
                    Negative
                  </div>
                </div>
              </div>
            </div>

            {/* Common Questions */}
            <div style={{
              background: 'rgba(15, 15, 35, 0.9)',
              border: '2px solid rgba(6, 182, 212, 0.3)',
              borderRadius: '24px',
              padding: '3rem',
              marginBottom: '3rem',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
            }}>
              <h3 className="hero-title" style={{
                fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>❓</span>
                Most Asked Questions
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {analysisResult.commonQuestions.map((question, idx) => (
                  <li key={idx} style={{
                    color: '#e5e7eb',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    fontSize: '1.1rem',
                    lineHeight: '1.6',
                    padding: '1rem',
                    background: 'rgba(6, 182, 212, 0.1)',
                    borderRadius: '15px',
                    border: '1px solid rgba(6, 182, 212, 0.2)'
                  }}>
                    <span style={{
                      color: '#06b6d4',
                      fontSize: '1.3rem',
                      minWidth: '1.3rem',
                      fontWeight: '900'
                    }}>{idx + 1}.</span>
                    <span>{question}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Key Feedback */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '24px',
              padding: '3rem',
              marginBottom: '3rem',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
            }}>
              <h3 className="hero-title" style={{
                fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>💬</span>
                Key Feedback & Suggestions
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {analysisResult.feedback.map((item, idx) => (
                  <li key={idx} style={{
                    color: '#e5e7eb',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    fontSize: '1.1rem',
                    lineHeight: '1.6'
                  }}>
                    <span style={{
                      color: '#8b5cf6',
                      fontSize: '1.3rem',
                      minWidth: '1.3rem'
                    }}>•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Export Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              marginBottom: '3rem',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`${API}/export-csv`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        analysis_data: {
                          videoTitle: analysisResult.videoTitle,
                          statistics: analysisResult.statistics,
                          commonQuestions: analysisResult.commonQuestions,
                          feedback: analysisResult.feedback,
                          insights: analysisResult.insights
                        }
                      })
                    });
                    const data = await response.json();
                    if (data.success) {
                      const blob = new Blob([data.csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = data.filename || 'comments-analysis.csv';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }
                  } catch (err) {
                    console.error('Export failed:', err);
                  }
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                📥 Export CSV
              </button>
            </div>

            {/* Actionable Insights */}
            <div>
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '800',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
                justifyContent: 'center'
              }}>
                <span style={{
                  fontSize: '1.8rem',
                  filter: 'drop-shadow(0 0 15px rgba(139, 92, 246, 0.7))'
                }}>💡</span>
                Actionable Insights
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '2rem'
              }}>
                {analysisResult.insights.map((insight, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(15, 15, 35, 0.9)',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '24px',
                    padding: '2.5rem',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.3s ease'
                  }}>
                    <h4 style={{
                      color: '#8b5cf6',
                      fontSize: '1.3rem',
                      fontWeight: '700',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>📌</span>
                      {insight.title}
                    </h4>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: '1rem',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      {insight.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsAnalysis;